import React, { useState, useMemo, useCallback } from "react";
import {
  IndexTable,
  Card,
  Page,
  IndexFilters,
  Badge,
  ChoiceList,
  Link,
  useSetIndexFiltersMode,
  Loading,
  Modal,
  useIndexResourceState,
  Tooltip,
  Spinner,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import PropertiesModal from "./PropertiesModal";
import { GroupData, Property, RecalculationStatus } from "../../types";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { TabsTableSkeleton } from "@shared/ui";
import { AppPageWrapper } from "@shared/ui";
import {
  ExportIcon,
  FormsIcon,
  ResetIcon,
  RefreshIcon,
} from "@medentic/polaris-icons";
import CreateFormModal from "./CreateFormModal";
import { getSensitivityLabel, getSensitivityTooltip } from "../../utils";
import { TranslateLink } from "@shared/ui";

const PatientProperties: React.FC = () => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property>();
  const { mode, setMode } = useSetIndexFiltersMode();
  const [showResetModal, setShowResetModal] = useState(false);
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);

  const [queryValue, setQueryValue] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);

  const { GET, DELETE, POST } = useApiRequest();
  const queryClient = useQueryClient();
  const params = useParams();
  const clinicId = params.clinicId;

  const {
    data: groupsData = [],
    isLoading: isSchemaLoading,
    isFetching: isSchemaFetching,
  } = useQuery<GroupData[]>({
    queryKey: ["patient-schema"],
    queryFn: () => GET(`/api/healthcare-companies/${clinicId}/patients/schema`),
  });

  const groups = useMemo(
    () => (groupsData.length ? groupsData[0]?.groups : []),
    [groupsData]
  );

  // Create a map of groupId to groupName
  const groupNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    groups.forEach((group) => {
      map[group.name] = group.label;
    });
    return map;
  }, [groups]);

  const { data: properties = [], isLoading } = useQuery<Property[]>({
    queryKey: ["patient-schema-properties"],
    queryFn: () =>
      GET(`/api/healthcare-companies/${clinicId}/patients/schema/properties`),
  });

  const tabs = useMemo(
    () => [
      {
        id: "all",
        content: `${t("properties.tabs.all")} (${properties.length})`,
      },
      {
        id: "calculated",
        content: `${t("properties.tabs.calculated")} (${
          properties.filter((p) => p.isCalculated).length
        })`,
      },
      {
        id: "readOnly",
        content: `${t("properties.tabs.readOnly")} (${
          properties.filter((p) => p.isReadOnlyValue).length
        })`,
      },
      {
        id: "custom",
        content: `${t("properties.tabs.custom")} (${
          properties.filter((p) => !p.isMedenticDefined).length
        })`,
      },
      {
        id: "archived",
        content: `${t("properties.tabs.archived")} (${
          properties.filter((p) => p.isHiddenDefinition).length
        })`,
      },
    ],
    [properties, t]
  );

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const handleOpenModal = (property?: Property) => {
    setEditingProperty(property);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProperty(undefined);
  };

  const filteredProperties = useMemo(() => {
    let filtered = properties;

    // Filter by query
    if (queryValue) {
      filtered = filtered.filter((property) =>
        property.label.toLowerCase().includes(queryValue.toLowerCase())
      );
    }

    // Filter by tabs
    if (selectedTab === 1) {
      filtered = filtered.filter((property) => property.isCalculated);
    } else if (selectedTab === 2) {
      filtered = filtered.filter((property) => property.isReadOnlyValue);
    } else if (selectedTab === 3) {
      filtered = filtered.filter((property) => !property.isMedenticDefined);
    } else if (selectedTab === 4) {
      filtered = filtered.filter((property) => property.isHiddenDefinition);
    }

    // Filter by group
    if (selectedGroup.length) {
      filtered = filtered.filter((property) =>
        selectedGroup.includes(property.groupId)
      );
    }

    // Filter by type
    if (selectedType.length) {
      filtered = filtered.filter(
        (property) =>
          property?.renderer && selectedType.includes(property?.renderer?.type)
      );
    }

    return filtered;
  }, [properties, queryValue, selectedGroup, selectedType, selectedTab]);

  const appliedFilters = [];
  if (selectedGroup.length > 0) {
    appliedFilters.push({
      key: "group",
      label: `${t("properties.filters.group")}: ${selectedGroup
        .map((group) => groupNameMap[group])
        .join(", ")}`,
      onRemove: () => setSelectedGroup([]),
    });
  }
  if (selectedType.length > 0) {
    appliedFilters.push({
      key: "type",
      label: `${t("properties.filters.type")}: ${selectedType.join(", ")}`,
      onRemove: () => setSelectedType([]),
    });
  }
  const handleClearAll = useCallback(() => {
    setQueryValue("");
    setSelectedGroup([]);
    setSelectedType([]);
  }, []);

  const handleExport = () => {
    // isReadOnlyDefinition = true can't be archived.
    // or properties that are isMedenticDefined = true.

    console.log("Export action triggered");
  };

  const handleGroups = () => {
    console.log("Groups action triggered");
  };

  const resetSchemaMutation = useMutation({
    mutationFn: () =>
      DELETE(`/api/healthcare-companies/${clinicId}/patients/schema`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-schema"] });
      queryClient.invalidateQueries({
        queryKey: ["patient-schema-properties"],
      });
      setShowResetModal(false);
    },
    onError: (error) => {
      console.error("Failed to reset schema:", error);
    },
  });

  const handleResetSchema = () => {
    resetSchemaMutation.mutate();
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(
      properties.map((property) => ({
        id: property.name,
      }))
    );

  const [isCreateFormModalOpen, setIsCreateFormModalOpen] = useState(false);

  const handleArchiveProperties = () => {
    const archivableProperties = selectedResources.filter((propertyName) => {
      const property = properties.find((p) => p.name === propertyName);
      return (
        property &&
        !property.isReadOnlyValue &&
        !property.isMedenticDefined &&
        !property.isHiddenDefinition
      );
    });

    archivableProperties.forEach((propertyName) => {
      DELETE(
        `/api/healthcare-companies/${clinicId}/patients/schema/properties/${propertyName}`
      )
        .then(() => {
          console.log(`Property ${propertyName} archived successfully.`);
          queryClient.invalidateQueries({
            queryKey: ["patient-schema-properties"],
          });
        })
        .catch((error) => {
          console.error(`Failed to archive property ${propertyName}:`, error);
        });
    });
  };

  // Add query for recalculation status
  const { data: recalculationStatus } = useQuery<RecalculationStatus>({
    queryKey: ["patient-schema-recalculation-status"],
    queryFn: () =>
      GET<RecalculationStatus>(
        `/api/healthcare-companies/${clinicId}/patients/schema/properties/recalculate`
      ),
    refetchInterval: (query) => {
      // If there's an active process, poll every 5 seconds
      return ["queued", "in_progress"].includes(
        query.state.data?.process?.status ?? ""
      )
        ? 5000
        : false;
    },
  });

  // Add mutation for starting recalculation
  const startRecalculationMutation = useMutation({
    mutationFn: () =>
      POST(
        `/api/healthcare-companies/${clinicId}/patients/schema/properties/recalculate`,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-schema-recalculation-status"],
      });
      setShowRecalculateModal(false);
    },
  });

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("properties.title")}
        primaryAction={{
          content: t("properties.addProperty"),
          onAction: () => handleOpenModal(),
        }}
        secondaryActions={[
          {
            content: t("properties.groups"),
            onAction: handleGroups,
            disabled: true,
          },
        ]}
        actionGroups={[
          {
            title: t("patients.moreActions"),
            actions: [
              {
                content: t("properties.createForm"),
                onAction: () => setIsCreateFormModalOpen(true),
                icon: FormsIcon,
              },
              {
                content: t("patients.exportSchema"),
                icon: ExportIcon,
                disabled: true,
                onAction: handleExport,
              },
              {
                content: t("patients.recomputeProperties"),
                icon: RefreshIcon,
                prefix:
                  !recalculationStatus?.canExecute ||
                  startRecalculationMutation.isPending ? (
                    <Spinner size="small" />
                  ) : undefined,
                disabled:
                  !recalculationStatus?.canExecute ||
                  startRecalculationMutation.isPending,
                onAction: () => setShowRecalculateModal(true),
              },
              {
                content: t("patients.resetSchema"),
                icon: ResetIcon,
                destructive: true,
                onAction: () => setShowResetModal(true),
              },
            ],
          },
        ]}
      >
        {isSchemaFetching && <Loading />}
        {isLoading || isSchemaLoading ? (
          <TabsTableSkeleton />
        ) : (
          <Card padding={"0"}>
            <IndexFilters
              canCreateNewView={false}
              tabs={tabs}
              selected={selectedTab}
              onSelect={handleTabChange}
              mode={mode}
              setMode={setMode}
              filters={[
                {
                  key: "group",
                  label: t("properties.filters.group"),
                  filter: (
                    <ChoiceList
                      title={t("properties.filters.group")}
                      titleHidden
                      choices={groups.map((group) => ({
                        label: groupNameMap[group.name],
                        value: group.name,
                      }))}
                      selected={selectedGroup}
                      onChange={setSelectedGroup}
                      allowMultiple
                    />
                  ),
                },
                {
                  key: "type",
                  label: t("properties.filters.type"),
                  filter: (
                    <ChoiceList
                      title={t("properties.filters.type")}
                      titleHidden
                      choices={[
                        { label: t("properties.filters.typeOptions.text"), value: "text" },
                        { label: t("properties.filters.typeOptions.number"), value: "number" },
                        { label: t("properties.filters.typeOptions.phone"), value: "phoneNumber" },
                      ]}
                      selected={selectedType}
                      onChange={setSelectedType}
                      allowMultiple
                    />
                  ),
                },
              ]}
              appliedFilters={appliedFilters}
              queryValue={queryValue}
              onQueryChange={setQueryValue}
              onQueryClear={() => setQueryValue("")}
              onQueryBlur={() => console.log("Query blur")}
              onClearAll={handleClearAll}
              cancelAction={{
                onAction: () => {},
                disabled: false,
                loading: false,
              }}
            />
            <IndexTable
              itemCount={filteredProperties.length}
              resourceName={{
                singular: t("common.property"),
                plural: t("common.properties"),
              }}
              selectedItemsCount={
                allResourcesSelected ? "All" : selectedResources.length
              }
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: t("properties.columns.name") },
                { title: t("properties.columns.type") },
                { title: t("properties.columns.group") },
                { title: t("properties.columns.isCustom") },
                { title: t("properties.columns.mandatory") },
                { title: t("properties.columns.inCreateForm") },
                { title: t("properties.columns.usedIn") },
                { title: t("properties.columns.sensitivity") },
              ]}
              selectable={selectedTab !== 3}
              promotedBulkActions={[
                {
                  content: t("properties.archive"),
                  onAction: handleArchiveProperties,
                },
              ]}
            >
              {filteredProperties.map(
                (
                  {
                    name,
                    label,
                    renderer,
                    groupId,
                    isMedenticDefined,
                    isRequired,
                    usage,
                    isHiddenDefinition,
                    isInCreateForm,
                    sensitivityLevel,
                  },
                  index
                ) => (
                  <IndexTable.Row
                    id={name}
                    key={name}
                    position={index}
                    selected={selectedResources.includes(name)}
                    onClick={
                      isHiddenDefinition
                        ? undefined
                        : () => {
                            handleOpenModal(filteredProperties[index]);
                          }
                    }
                    disabled={isHiddenDefinition}
                  >
                    <IndexTable.Cell>{label}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {t(`properties.type.${renderer?.id}`)}
                    </IndexTable.Cell>
                    <IndexTable.Cell>{groupNameMap[groupId]}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge
                        progress={isMedenticDefined ? "incomplete" : "complete"}
                      >
                        {isMedenticDefined ? t("common.no") : t("common.yes")}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge
                        progress={isInCreateForm ? "complete" : "incomplete"}
                      >
                        {isInCreateForm ? t("common.yes") : t("common.no")}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge progress={isRequired ? "complete" : "incomplete"}>
                        {isRequired ? t("common.yes") : t("common.no")}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {usage && usage > 0 ? (
                        <div onClick={(event) => event.stopPropagation()}>
                          <Link>{usage}</Link>
                        </div>
                      ) : (
                        usage
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Tooltip
                        content={t(getSensitivityTooltip(sensitivityLevel))}
                      >
                        <p className="tooltip_container">
                          <Badge
                            progress={
                              sensitivityLevel ? "complete" : "incomplete"
                            }
                            tone={sensitivityLevel ? "warning" : undefined}
                          >
                            {t(getSensitivityLabel(sensitivityLevel))}
                          </Badge>
                        </p>
                      </Tooltip>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                )
              )}
            </IndexTable>
          </Card>
        )}

        {isModalOpen && (
          <PropertiesModal
            open={isModalOpen}
            onClose={handleCloseModal}
            initialData={editingProperty}
          />
        )}

        {isCreateFormModalOpen && (
          <CreateFormModal
            open={isCreateFormModalOpen}
            onClose={() => setIsCreateFormModalOpen(false)}
            properties={properties}
            clinicId={clinicId ?? ""}
          />
        )}
      </Page>
      <br />
      {showResetModal && (
        <Modal
          open={showResetModal}
          onClose={() => setShowResetModal(false)}
          title={t("patients.resetSchemaModal.title")}
          primaryAction={{
            content: t("patients.resetSchemaModal.cta.primary"),
            destructive: true,
            onAction: handleResetSchema,
            loading: resetSchemaMutation.isPending,
          }}
          secondaryActions={[
            {
              content: t("patients.resetSchemaModal.cta.secondary"),
              onAction: () => setShowResetModal(false),
              disabled: resetSchemaMutation.isPending,
            },
          ]}
        >
          <Modal.Section>
            {t("patients.resetSchemaModal.description")}
          </Modal.Section>
        </Modal>
      )}

      {showRecalculateModal && (
        <Modal
          open={showRecalculateModal}
          onClose={() => setShowRecalculateModal(false)}
          title={t("patients.recalculatePropertiesModal.title")}
          primaryAction={{
            content: t("patients.recalculatePropertiesModal.cta.primary"),
            onAction: () => startRecalculationMutation.mutate(),
            loading: startRecalculationMutation.isPending,
          }}
          secondaryActions={[
            {
              content: t("common.cancel"),
              onAction: () => setShowRecalculateModal(false),
              disabled: startRecalculationMutation.isPending,
              loading: startRecalculationMutation.isPending,
            },
          ]}
        >
          <Modal.Section>
            <TranslateLink
              text={t(
                "patients.recalculatePropertiesModal.description"
              ).replace(/\\n/g, "  \n")}
              tone="base"
            />
            {recalculationStatus?.recordsToProcess && (
              <TranslateLink
                tone="base"
                text={t("patients.recalculatePropertiesModal.estimatedTime", {
                  count: recalculationStatus.recordsToProcess,
                  seconds: Math.ceil(
                    recalculationStatus.executionTimeSeconds /
                      recalculationStatus.recordsToProcess
                  ),
                })}
              />
            )}
            {recalculationStatus?.lastTime && (
              <TranslateLink
                tone="base"
                text={t(
                  "patients.recalculatePropertiesModal.lastRecalculated",
                  {
                    time: new Date(
                      recalculationStatus.lastTime
                    ).toLocaleString(),
                  }
                )}
              />
            )}
          </Modal.Section>
        </Modal>
      )}
    </AppPageWrapper>
  );
};

export default PatientProperties;
